"use client";

/**
 * P9-01 · Browser event emitter.
 *
 * Queues events and flushes them in batches: a debounced timer during normal
 * browsing, and `navigator.sendBeacon` on `pagehide` / tab-hide to attempt delivery of the tail during navigation. Falls back to `fetch(keepalive)` where beacon
 * is unavailable or refuses delivery. A bounded in-memory retry preserves IDs.
 * Every failure is swallowed — analytics must never throw into
 * the UI. Consent bucket and attribution are read from the page, not the server.
 */
import { readClientCookie } from "~/lib/cookies/client-cookie";
import { ensureAnonId } from "./anon-id";
import { eventInputSchema, MAX_BATCH_EVENTS } from "./intake";
import {
  analyticsGranted,
  parseConsentBucket,
  VX_CONSENT_COOKIE,
  type Attribution,
  type ClientEvent,
  type ClientEventType,
  type ConsentBucket,
} from "./events";

const ENDPOINT = "/api/analytics/track";
const FLUSH_DELAY_MS = 2_000;

const MAX_QUEUED_EVENTS = 100;
const MAX_PAYLOAD_BYTES = 48_000;
// One in-memory retry; event IDs survive uncertain first-party delivery.
type QueuedEvent = { event: ClientEvent; attempts: number };
let queue: QueuedEvent[] = [];
let inFlight = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let attribution: Attribution | null = null;
let bound = false;

/**
 * P11-10 · Absent / unrecognised cookie → `none`. This was `analytics`
 * (capture-by-default) until a banner existed to ask the question.
 */
function consentBucket(): ConsentBucket {
  try {
    return parseConsentBucket(readClientCookie(VX_CONSENT_COOKIE));
  } catch {
    return "none";
  }
}

function captureAttribution(): Attribution {
  const params = new URLSearchParams(window.location.search);
  const attr: Attribution = {
    referrer: document.referrer.slice(0, 2048) || undefined,
    landingPath: window.location.pathname.slice(0, 2048) || undefined,
  };
  const map: Record<string, keyof Attribution> = {
    utm_source: "utmSource",
    utm_medium: "utmMedium",
    utm_campaign: "utmCampaign",
    utm_term: "utmTerm",
    utm_content: "utmContent",
  };
  for (const [param, key] of Object.entries(map)) {
    const v = params.get(param);
    if (v) attr[key] = v.slice(0, 200);
  }
  return attr;
}

function bindLifecycle() {
  if (bound || typeof window === "undefined") return;
  bound = true;
  attribution = captureAttribution();
  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}

function flush(useBeacon = false): void {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  const bucket = consentBucket();
  if (!analyticsGranted(bucket)) { queue = []; return; }
  if (!queue.length) return;
  if (inFlight) { scheduleFlush(); return; }

  const batch: QueuedEvent[] = [];
  const encode = () => JSON.stringify({ events: batch.map(item => item.event), consentBucket: bucket, attribution: attribution ?? undefined });
  let payload = "";
  while (queue.length && batch.length < MAX_BATCH_EVENTS) {
    const next = queue.shift()!;
    batch.push(next);
    let candidate: string;
    try {
      candidate = encode();
    } catch {
      // Malformed caller props must not strand the rest of the queue.
      batch.pop();
      continue;
    }
    if (new Blob([candidate]).size > MAX_PAYLOAD_BYTES) {
      batch.pop();
      // One oversized event is discarded without blocking later normal events.
      if (!batch.length) continue;
      queue.unshift(next);
      break;
    }
    payload = candidate;
  }
  if (!batch.length) { if (queue.length) scheduleFlush(); return; }
  inFlight = true;
  void deliver(payload, useBeacon).then(accepted => {
    if (!accepted && analyticsGranted(consentBucket())) {
      const retry = batch.filter(item => item.attempts === 0).map(item => ({ ...item, attempts: 1 }));
      queue = [...retry, ...queue].slice(0, MAX_QUEUED_EVENTS);
    }
  }).catch(() => undefined).finally(() => {
    inFlight = false;
    if (queue.length) scheduleFlush();
  });
}

async function deliver(payload: string, useBeacon: boolean): Promise<boolean> {
  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      if (navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "text/plain" }))) return true;
    } catch { /* Continue to the supported keepalive fallback. */ }
  }
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const controller = new AbortController();
    timeout = setTimeout(() => controller.abort(), 5_000);
    const response = await fetch(ENDPOINT, {
      method: "POST", body: payload, keepalive: true,
      headers: { "content-type": "text/plain" }, signal: controller.signal,
    });
    return response.ok;
  } catch { return false; }
  finally { if (timeout !== undefined) clearTimeout(timeout); }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_DELAY_MS);
}

function newEventId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Enqueue one event. Fire-and-forget. */
export function track(
  type: ClientEventType,
  payload?: Omit<ClientEvent, "type" | "eventId">,
): void {
  try {
    if (typeof window === "undefined") return;
    // P11-10 · The gate, at the earliest possible point. Dropping the event here
    // rather than server-side means nothing is queued, no beacon is sent, and no
    // row is written — a visitor who hasn't consented (or has declined) leaves no
    // behavioural trace at all, which is the whole point. Previously every event
    // reached the server and was stored with a `consentBucket` label.
    if (!analyticsGranted(consentBucket())) { queue = []; return; }
    // P17A-06 · Mint `vx_anon` here rather than at the edge, so the storefront's
    // first response carries no `Set-Cookie` and can be stored by a shared cache
    // (`src/lib/analytics/anon-id.ts` has the whole argument). This is the single
    // gate every surface that needs the id passes through: the cart's
    // ADD_TO_CART, the wishlist's WISHLIST_ADD and the page view all land here
    // before the batch is flushed, so the POST that needs the cookie has it.
    //
    // Being INSIDE the consent gate is deliberate and is a small behaviour
    // change: the middleware minted for everyone, so a visitor who declined
    // analytics still got an id, and `checkout-service.ts` still created an
    // `AnalyticsSession` for them at payment-intent time. Now nobody who
    // declined has an id to be recorded against.
    ensureAnonId();
    bindLifecycle();
    // A shared id so the server's third-party forwarding (P9-05) de-dupes against
    // any browser tag firing the same event.
    const eventId = newEventId();
    if (queue.length >= MAX_QUEUED_EVENTS) return;
    // Snapshot caller props so later mutation cannot poison a queued batch.
    const encodedEvent = JSON.stringify({ ...payload, type, eventId });
    if (new Blob([encodedEvent]).size > MAX_PAYLOAD_BYTES) return;
    const parsed = eventInputSchema.safeParse(JSON.parse(encodedEvent));
    // JSON-safe caller values can still violate intake bounds. Drop only that
    // event so a malformed search or quantity cannot reject the whole batch.
    if (!parsed.success) return;
    queue.push({ event: parsed.data, attempts: 0 });
    scheduleFlush();
  } catch {
    // Cookie, identifier and caller payload failures cannot break the UI.
  }
}
